import * as React from "react";
import {
  Field,
  Input,
  Spinner,
  Caption1,
  Badge,
  Button,
  tokens,
  makeStyles,
} from "@fluentui/react-components";
import { Dismiss12Regular, CheckmarkCircle16Filled, Warning16Filled } from "@fluentui/react-icons";
import { GlpiUser } from "../types/glpi";
import { formatUser } from "../hooks/useTechnicians";
import { useRequesterSearch } from "../hooks/useRequesterSearch";

interface Props {
  /** Liste des demandeurs sélectionnés. */
  value: GlpiUser[];
  onChange: (next: GlpiUser[]) => void;
  /** Email expéditeur (pour le badge de détection auto). */
  senderEmail?: string;
  /** Résultat de l'auto-détection : présent si trouvé, null si recherche terminée mais aucun, undefined si en cours. */
  autoFoundUser: GlpiUser | null | undefined;
  autoLoading: boolean;
  required?: boolean;
  error?: string;
}

const useStyles = makeStyles({
  chips: {
    display: "flex",
    flexWrap: "wrap",
    gap: "4px",
    marginTop: "4px",
  },
  chip: {
    display: "inline-flex",
    alignItems: "center",
    gap: "4px",
    paddingLeft: "8px",
    paddingRight: "4px",
    paddingTop: "2px",
    paddingBottom: "2px",
    backgroundColor: tokens.colorNeutralBackground3,
    borderRadius: tokens.borderRadiusMedium,
    fontSize: "12px",
  },
  results: {
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    borderRadius: tokens.borderRadiusMedium,
    maxHeight: "160px",
    overflowY: "auto",
    marginTop: "4px",
    backgroundColor: tokens.colorNeutralBackground1,
  },
  resultItem: {
    padding: "6px 8px",
    cursor: "pointer",
    ":hover": {
      backgroundColor: tokens.colorNeutralBackground2,
    },
  },
  autoBadge: {
    display: "inline-flex",
    alignItems: "center",
    gap: "4px",
    marginTop: "4px",
  },
});

export function RequesterPicker({
  value,
  onChange,
  senderEmail,
  autoFoundUser,
  autoLoading,
  required,
  error,
}: Props): React.ReactElement {
  const styles = useStyles();
  const [query, setQuery] = React.useState("");
  const { results, loading } = useRequesterSearch(query);

  function addUser(u: GlpiUser): void {
    if (value.some((v) => v.id === u.id)) return;
    onChange([...value, u]);
    setQuery("");
  }

  function removeUser(id: number): void {
    onChange(value.filter((v) => v.id !== id));
  }

  return (
    <Field
      label="Demandeur(s)"
      required={required}
      validationState={error ? "error" : "none"}
      validationMessage={error}
    >
      {senderEmail && (
        <div className={styles.autoBadge}>
          {autoLoading ? (
            <>
              <Spinner size="tiny" />
              <Caption1>Recherche pour {senderEmail}…</Caption1>
            </>
          ) : autoFoundUser ? (
            <Badge appearance="filled" color="success" icon={<CheckmarkCircle16Filled />}>
              Détecté : {formatUser(autoFoundUser)}
            </Badge>
          ) : (
            <Badge appearance="filled" color="warning" icon={<Warning16Filled />}>
              Aucun utilisateur trouvé pour {senderEmail}
            </Badge>
          )}
        </div>
      )}

      <div className={styles.chips}>
        {value.map((u) => (
          <span key={u.id} className={styles.chip}>
            {formatUser(u)}
            <Button
              appearance="subtle"
              size="small"
              icon={<Dismiss12Regular />}
              aria-label={`Retirer ${formatUser(u)}`}
              onClick={() => removeUser(u.id)}
            />
          </span>
        ))}
      </div>

      <Input
        placeholder="Ajouter un demandeur (nom ou login)…"
        value={query}
        onChange={(_, d) => setQuery(d.value)}
        contentAfter={loading ? <Spinner size="tiny" /> : undefined}
      />

      {query.length >= 2 && results.length > 0 && (
        <div className={styles.results} role="listbox">
          {results.map((u) => (
            <div
              key={u.id}
              className={styles.resultItem}
              role="option"
              aria-selected={value.some((v) => v.id === u.id)}
              tabIndex={0}
              onClick={() => addUser(u)}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  addUser(u);
                }
              }}
            >
              {formatUser(u)}
              {u.email && (
                <Caption1 style={{ display: "block", color: tokens.colorNeutralForeground3 }}>
                  {u.email}
                </Caption1>
              )}
            </div>
          ))}
        </div>
      )}
    </Field>
  );
}
